import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  Send, Users, Settings, MoreVertical, UserPlus, LogOut,
  Trash2, Edit2, X, Check, Search, Phone, Video
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';
import { BACKEND_URL } from '@/config/api';

export default function GroupChatPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // State
  const [group, setGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [showAddMember, setShowAddMember] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Fetch group details
  const fetchGroup = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/groups/${groupId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGroup(data);
        setEditName(data.name);
        setEditDescription(data.description || '');
      } else if (response.status === 404) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching group:', error);
    }
  }, [groupId, token, navigate]);

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/groups/${groupId}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setIsLoading(false);
    }
  }, [groupId, token]);

  // Initial load
  useEffect(() => {
    if (groupId && token) {
      fetchGroup();
      fetchMessages();

      // Poll for new messages
      pollIntervalRef.current = setInterval(fetchMessages, 3000);

      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [groupId, token, fetchGroup, fetchMessages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send message
  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || isSending) return;

    setIsSending(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/groups/${groupId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: newMessage })
      });

      if (response.ok) {
        const message = await response.json();
        setMessages(prev => [...prev, message]);
        setNewMessage('');
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSending(false);
    }
  };

  // Update group
  const updateGroup = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/groups/${groupId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editName,
          description: editDescription || null
        })
      });

      if (response.ok) {
        setGroup(prev => ({ ...prev, name: editName, description: editDescription }));
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error updating group:', error);
    }
  };

  // Leave group
  const leaveGroup = async () => {
    if (!window.confirm('Are you sure you want to leave this group?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/groups/${groupId}/leave`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error leaving group:', error);
    }
  };

  // Delete group (owner only)
  const deleteGroup = async () => {
    if (!window.confirm('Are you sure you want to delete this group? This cannot be undone.')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/groups/${groupId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error deleting group:', error);
    }
  };

  // Search users to add
  useEffect(() => {
    const searchUsers = async () => {
      if (!searchQuery.trim() || searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }

      try {
        const response = await fetch(
          `${BACKEND_URL}/api/users/search?q=${encodeURIComponent(searchQuery)}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (response.ok) {
          const users = await response.json();
          // Filter out existing members
          const filtered = users.filter(
            u => !group?.member_ids?.includes(u.id)
          );
          setSearchResults(filtered);
        }
      } catch (error) {
        console.error('Error searching users:', error);
      }
    };

    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, token, group?.member_ids]);

  // Add member
  const addMember = async (userId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/groups/${groupId}/members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_ids: [userId] })
      });

      if (response.ok) {
        fetchGroup();
        setSearchQuery('');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error adding member:', error);
    }
  };

  // Remove member
  const removeMember = async (userId) => {
    if (!window.confirm('Remove this member from the group?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/groups/${groupId}/members/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchGroup();
      }
    } catch (error) {
      console.error('Error removing member:', error);
    }
  };

  const isOwner = group?.owner_id === user?.id;
  const isAdmin = group?.members?.find(m => m.user_id === user?.id)?.role === 'admin';

  if (isLoading) {
    return (
      <div className={cn(
        "flex-1 flex items-center justify-center ml-[352px]",
        isDark ? "bg-gray-900" : "bg-gray-50"
      )}>
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className={cn(
      "flex-1 flex flex-col h-screen ml-[352px]",
      isDark ? "bg-gray-900" : "bg-white"
    )} data-testid="group-chat-page">
      {/* Header */}
      <div className={cn(
        "h-16 flex items-center justify-between px-4 border-b",
        isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      )}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Users size={20} className="text-white" />
          </div>
          <div>
            <h1 className={cn(
              "font-semibold",
              isDark ? "text-white" : "text-gray-900"
            )}>
              {group?.name}
            </h1>
            <p className={cn(
              "text-sm",
              isDark ? "text-gray-400" : "text-gray-500"
            )}>
              {group?.members?.length || 0} members
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowMembers(!showMembers)}
            className={showMembers ? 'bg-blue-100 dark:bg-blue-900/30' : ''}
          >
            <Users size={20} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowSettings(!showSettings)}
          >
            <Settings size={20} />
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Messages */}
        <div className="flex-1 flex flex-col">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <Users size={48} className={cn(
                  "mx-auto mb-4",
                  isDark ? "text-gray-600" : "text-gray-300"
                )} />
                <p className={isDark ? "text-gray-400" : "text-gray-500"}>
                  No messages yet. Start the conversation!
                </p>
              </div>
            )}

            {messages.map((message, index) => {
              const isOwnMessage = message.sender_id === user?.id;
              const showAvatar = index === 0 || 
                messages[index - 1]?.sender_id !== message.sender_id;

              return (
                <div
                  key={message.id}
                  className={cn(
                    "flex gap-3",
                    isOwnMessage && "flex-row-reverse"
                  )}
                >
                  {showAvatar ? (
                    <div
                      className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-medium"
                      style={{ backgroundColor: message.sender_avatar_color || '#6366F1' }}
                    >
                      {message.sender_name?.charAt(0).toUpperCase()}
                    </div>
                  ) : (
                    <div className="w-8" />
                  )}

                  <div className={cn(
                    "max-w-[70%]",
                    isOwnMessage && "text-right"
                  )}>
                    {showAvatar && !isOwnMessage && (
                      <p className={cn(
                        "text-xs mb-1",
                        isDark ? "text-gray-400" : "text-gray-500"
                      )}>
                        {message.sender_name}
                      </p>
                    )}
                    <div className={cn(
                      "px-4 py-2 rounded-2xl inline-block text-left",
                      isOwnMessage
                        ? "bg-blue-600 text-white"
                        : isDark
                          ? "bg-gray-700 text-white"
                          : "bg-gray-100 text-gray-900"
                    )}>
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                    <p className={cn(
                      "text-xs mt-1",
                      isDark ? "text-gray-500" : "text-gray-400"
                    )}>
                      {new Date(message.created_at).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <form
            onSubmit={sendMessage}
            className={cn(
              "p-4 border-t",
              isDark ? "border-gray-700" : "border-gray-200"
            )}
          >
            <div className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                className="flex-1"
                data-testid="group-message-input"
              />
              <Button type="submit" disabled={!newMessage.trim() || isSending} data-testid="send-group-message-btn">
                <Send size={18} />
              </Button>
            </div>
          </form>
        </div>

        {/* Members Panel */}
        {showMembers && (
          <div className={cn(
            "w-72 border-l overflow-y-auto",
            isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
          )}>
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className={cn(
                  "font-semibold",
                  isDark ? "text-white" : "text-gray-900"
                )}>
                  Members ({group?.members?.length || 0})
                </h3>
                {(isOwner || isAdmin) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAddMember(!showAddMember)}
                  >
                    <UserPlus size={16} />
                  </Button>
                )}
              </div>

              {/* Add Member Search */}
              {showAddMember && (
                <div className="mb-4">
                  <div className="relative mb-2">
                    <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search users..."
                      className="pl-8 text-sm"
                    />
                  </div>
                  {searchResults.length > 0 && (
                    <div className={cn(
                      "rounded-lg border max-h-32 overflow-y-auto",
                      isDark ? "border-gray-700" : "border-gray-200"
                    )}>
                      {searchResults.map(u => (
                        <button
                          key={u.id}
                          onClick={() => addMember(u.id)}
                          className={cn(
                            "w-full flex items-center gap-2 p-2 text-left",
                            isDark ? "hover:bg-gray-700" : "hover:bg-gray-100"
                          )}
                        >
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs"
                            style={{ backgroundColor: u.avatar_color || '#6366F1' }}
                          >
                            {u.name?.charAt(0).toUpperCase()}
                          </div>
                          <span className={cn(
                            "text-sm truncate",
                            isDark ? "text-gray-200" : "text-gray-700"
                          )}>
                            {u.name}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Member List */}
              <div className="space-y-2">
                {group?.members?.map(member => (
                  <div
                    key={member.user_id}
                    className={cn(
                      "flex items-center gap-3 p-2 rounded-lg",
                      isDark ? "hover:bg-gray-700" : "hover:bg-gray-100"
                    )}
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                      style={{ backgroundColor: member.avatar_color || '#6366F1' }}
                    >
                      {member.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn(
                        "text-sm font-medium truncate",
                        isDark ? "text-white" : "text-gray-900"
                      )}>
                        {member.name}
                        {member.user_id === user?.id && ' (You)'}
                      </p>
                      <p className={cn(
                        "text-xs",
                        isDark ? "text-gray-400" : "text-gray-500"
                      )}>
                        {member.user_id === group?.owner_id ? 'Owner' : member.role}
                      </p>
                    </div>
                    {(isOwner || isAdmin) && member.user_id !== user?.id && member.user_id !== group?.owner_id && (
                      <button
                        onClick={() => removeMember(member.user_id)}
                        className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Settings Panel */}
        {showSettings && (
          <div className={cn(
            "w-72 border-l overflow-y-auto",
            isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
          )}>
            <div className="p-4 space-y-4">
              <h3 className={cn(
                "font-semibold",
                isDark ? "text-white" : "text-gray-900"
              )}>
                Group Settings
              </h3>

              {/* Edit Group Info */}
              {(isOwner || isAdmin) && (
                <div className="space-y-3">
                  {isEditing ? (
                    <>
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Group name"
                      />
                      <Input
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder="Description"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={updateGroup}>
                          <Check size={14} className="mr-1" />
                          Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
                          Cancel
                        </Button>
                      </div>
                    </>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => setIsEditing(true)}
                    >
                      <Edit2 size={16} className="mr-2" />
                      Edit Group Info
                    </Button>
                  )}
                </div>
              )}

              {/* Description */}
              {group?.description && (
                <div>
                  <p className={cn(
                    "text-xs font-medium mb-1",
                    isDark ? "text-gray-400" : "text-gray-500"
                  )}>
                    Description
                  </p>
                  <p className={cn(
                    "text-sm",
                    isDark ? "text-gray-300" : "text-gray-700"
                  )}>
                    {group.description}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                  onClick={leaveGroup}
                >
                  <LogOut size={16} className="mr-2" />
                  Leave Group
                </Button>

                {isOwner && (
                  <Button
                    variant="outline"
                    className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                    onClick={deleteGroup}
                  >
                    <Trash2 size={16} className="mr-2" />
                    Delete Group
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
